import { useState } from 'react';
import { Button, Badge } from '@/components/ui/PageHeader';
import { providerReach, CONTACT_SOURCES, TAP_KINDS, type ContactSource, type ContactTap } from '@/api/provider-reach';

// ═══════════════════════════════════════════════════════════════════
// A member's side of a business's reach, on its page.
//
// The door: which way the member came (search, an answer, a haat…). A
// conversation keeps the door of its first message; a tap carries its own.
// A tap is kept whoever sends it, and `counted` says whether it can ever be
// a lead: never for a signed-out caller, a test member or the business's own.
// ═══════════════════════════════════════════════════════════════════

export function MemberReachPanel({ providerId, signedIn }: { providerId: string; signedIn: boolean }) {
  const [door, setDoor] = useState<ContactSource | ''>('search');
  const [taps, setTaps] = useState<ContactTap[]>([]);
  const [body, setBody] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const source = door || undefined;

  async function tap(kind: (typeof TAP_KINDS)[number]) {
    setMessage(null);
    try {
      const res = await providerReach.contactTap(providerId, kind, source);
      setTaps((old) => [res.data, ...old]);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Tap failed');
    }
  }

  async function toggleAnswers(hide: boolean) {
    setMessage(null);
    try {
      const res = hide ? await providerReach.hideAnswers(providerId) : await providerReach.showAnswers(providerId);
      setMessage(res.data.hidden ? "You will not see this business's answers." : "You see this business's answers.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Failed');
    }
  }

  async function send() {
    setMessage(null);
    try {
      const res = await providerReach.messageWithDoor(providerId, body, source);
      setBody('');
      setMessage(`Sent in conversation ${res.data.threadId}. Its door is the first message's.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Send failed');
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <h2 className="text-lg font-semibold text-gray-900">Get in touch, through a door</h2>
      <label className="text-sm text-gray-700 flex items-center gap-2">
        Door
        <select
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={door}
          onChange={(e) => setDoor(e.target.value as ContactSource | '')}
        >
          <option value="">(not said)</option>
          {CONTACT_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </label>

      <div className="flex gap-2 flex-wrap">
        {TAP_KINDS.map((k) => (
          <Button key={k} variant="secondary" onClick={() => tap(k)}>Tap {k}</Button>
        ))}
      </div>
      {taps.length > 0 && (
        <ul className="text-xs text-gray-600 space-y-1">
          {taps.map((t) => (
            <li key={t.id} className="flex gap-2 items-center">
              <span>{t.kind} · {t.source ?? 'no door'}</span>
              <Badge variant={t.counted ? 'green' : 'gray'}>{t.counted ? 'counted' : 'kept, not counted'}</Badge>
            </li>
          ))}
        </ul>
      )}

      {signedIn && (
        <div className="flex gap-2">
          <input
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="A first message, with the door above"
          />
          <Button onClick={send} disabled={!body.trim()}>Send</Button>
        </div>
      )}
      {signedIn && (
        <div className="flex gap-2 items-center border-t border-gray-100 pt-3">
          <span className="text-sm text-gray-700">This business&apos;s answers in Curios:</span>
          <Button size="sm" variant="secondary" onClick={() => toggleAnswers(true)}>Hide them</Button>
          <Button size="sm" variant="secondary" onClick={() => toggleAnswers(false)}>Show them</Button>
        </div>
      )}
      {message && <p className="text-sm text-gray-700">{message}</p>}
    </section>
  );
}
